import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FileUploader, FileItem } from 'ng2-file-upload';
import { ISession } from '../../../models/session';
import { environment as config } from '../../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { interval } from 'rxjs/observable/interval';
import { switchMap, takeUntil } from 'rxjs/operators';
import { IUploadStatus, VideoService } from '../video.service';
import { Subject } from 'rxjs/Subject';
import { AuthService } from '../../auth/auth.service';

const limit = 5368709120; // 5Gb

@Component({
  selector: 'video-file-upload',
  templateUrl: './video-file-upload.component.html',
  styleUrls: ['./video-file-upload.component.scss']
})
export class VideoFileUploadComponent implements OnInit, OnDestroy {
  public uploader: FileUploader;
  public error: string;
  public filename: string;
  public hasBaseDropZoneOver = false;
  public uploadStatus: IUploadStatus = null;
  private pullVideoStats$;
  private pullVideoStatsStop$ = new Subject();

  @Input('accept') private accept: string;
  @Input('session') private session: ISession;
  @Input('login') private login: string;
  @Output() private onStartUpload: EventEmitter<any> = new EventEmitter<any>();
  @Output() private onSuccessfulUpload: EventEmitter<any> = new EventEmitter<any>();

  constructor(private translateService: TranslateService, private videoService: VideoService) {
    this.uploader = new FileUploader({
      url: `${config.screencastCustom.url}/upload`,
      disableMultipart: false,
      isHTML5: true,
      method: 'PUT',
      itemAlias: 'Filedata',
      allowedMimeType: ['video/mp4'],
      authToken: `Bearer ${AuthService.retrieve(AuthService.TOKEN_KEY)}`
    });
    this.uploader.onBuildItemForm = (fileItem: FileItem, form: any) => {
      form.append('login', this.login);
      form.append('sessionId', this.session.id);
      fileItem.file.name = this.filename;
    };
    this.uploader.onAfterAddingFile = (fileItem: FileItem) => {
      this.error = null;
      this.filename = fileItem.file.name;
      if (fileItem.file.size > limit) {
        this.error = this.translateService.instant('sessions.upload.video.warn_too_big');
      }
      // keep only one file in upload queue
      if (this.uploader.queue.length > 1) {
        this.uploader.removeFromQueue(this.uploader.queue[0]);
      }
      this.uploadStatus = null;
    };
    this.uploader.onSuccessItem = (item: FileItem, response: string, status: number) => {
      if (status === 200) {
        this.checkVideoStats();
      }
    };
    this.uploader.onErrorItem = (item: FileItem, response: string, status: number) => {
      this.error =
        status === 413
          ? this.translateService.instant('sessions.upload.video.error_too_big')
          : this.translateService.instant('sessions.upload.video.error_unknown');
    };
    this.uploader.onBeforeUploadItem = () => {
      this.onStartUpload.next();
    };
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  public upload(): void {
    this.error = null;
    if (this.uploader.queue.length > 0) {
      this.uploader.queue[0].upload();
    }
  }

  public canUpload(): boolean {
    return this.uploader.queue.length > 0 && !this.inProgressOrDone();
  }

  public cancel(): void {
    if (this.uploader.queue.length > 0) {
      this.uploader.queue[0].cancel();
    }
  }

  public canCancel(): boolean {
    return this.uploader.queue.length > 0 && this.uploader.queue[0].isUploading;
  }

  public canEditFilename(): boolean {
    return this.uploader.queue.length > 0 && !this.inProgressOrDone();
  }

  public inProgressOrDone(): boolean {
    const item = this.uploader.queue[0];
    return item ? item.isReady || item.isUploading || item.isSuccess : false;
  }

  public isPreparing(): boolean {
    const item = this.uploader.queue[0];
    return item ? item.isUploading : false;
  }

  public status(): string {
    if (this.isPreparing()) {
      return this.translateService.instant('sessions.upload.video.status.preparing');
    }
    if (this.uploadStatus && this.uploadStatus.status === 'UPLOADING') {
      return this.translateService.instant('sessions.upload.video.status.uploading');
    }
    if (this.uploadStatus && this.uploadStatus.status === 'UPLOADED') {
      return this.translateService.instant('sessions.upload.video.status.uploaded');
    }
    return '';
  }

  ngOnInit() {
    this.checkVideoStats();
  }

  ngOnDestroy(): void {
    this.pullVideoStatsStop$.unsubscribe();
    if (this.pullVideoStats$) {
      this.pullVideoStats$.unsubscribe();
    }
  }

  private resolveUploadedCustomFilename(status: IUploadStatus) {
    if (status.name) {
      const r = new RegExp(`^custom\\.${this.login}\\.${this.session.id}_(.*)$`);
      const m = status.name.match(r);
      this.filename = m ? m[1] : undefined;
    }
  }

  private checkVideoStats() {
    this.videoService.getStatus(this.session.id).subscribe((status: IUploadStatus) => {
      this.uploadStatus = status || <IUploadStatus>{};
      this.resolveUploadedCustomFilename(this.uploadStatus);
      if (this.uploadStatus.status && this.uploadStatus.status !== 'UPLOADED') {
        if (this.pullVideoStats$) {
          this.pullVideoStats$.unsubscribe();
        }
        this.pullVideoStats$ = interval(2000)
          .pipe(
            switchMap(() => this.videoService.getStatus(this.session.id)),
            takeUntil(this.pullVideoStatsStop$)
          )
          .subscribe((updatedStatus: IUploadStatus) => {
            this.uploadStatus = updatedStatus || <IUploadStatus>{};
            this.resolveUploadedCustomFilename(this.uploadStatus);
            if (this.uploadStatus.status && this.uploadStatus.status === 'UPLOADED') {
              this.onSuccessfulUpload.next();
            }
            if (
              !this.uploadStatus.status ||
              (this.uploadStatus.status && this.uploadStatus.status === 'UPLOADED')
            ) {
              this.pullVideoStatsStop$.next(true);
            }
          });
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/file-upload/video-file-upload.component.ts